import { createSignal, onMount, For, Show } from "solid-js";
import { useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import Text from '../components/Text';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { Client, OAuthClientType } from '../models/OAuthClient';
import { formatDateTime } from '../services/dateFormat';

export default function MyOAuthClients() {

    const api = useApi();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();

    const [clients, setClients] = createSignal<Client[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [newClient, setNewClient] = createSignal<Client>({
        id: "",
        clientName: "",
        clientType: OAuthClientType.Public,
        createdAt: ""
    });

    onMount(fetchClients);

    async function fetchClients() {
        const data = await api.oauth.getClients();
        if (isOk(data.data)) {
            const value = data.data.value as unknown as { clients?: Client[] } | Client[] | undefined;
            const list = Array.isArray(value) ? value : (value?.clients ?? []);
            setClients(list ?? []);
        }
        setLoading(false);
    }

    function submitNewClient(e: Event) {
        e.preventDefault();

        api.oauth.createClient({
            name: newClient().clientName,
            clientType: newClient().clientType ?? OAuthClientType.Public,
        }).then(data => {
            if (data.status == 200) {
                setClients([...clients(), {
                    id: "",
                    clientName: newClient().clientName,
                    clientType: newClient().clientType,
                    createdAt: ""
                }]);
                setNewClient({
                    id: "",
                    clientName: "",
                    clientType: OAuthClientType.Public,
                    createdAt: ""
                });
                fetchClients();
                toast?.info(t('OAUTH_CLIENT_CREATED'));
            }
            else {
                toast?.error(t('OAUTH_CLIENT_CREATE_FAILED'));
            }
        });
    }

    async function deleteClient(id: string) {
        if (!await showConfirm(t('OAUTH_CLIENT_DELETE_CONFIRM'), true)) return;
        api.oauth.deleteClient(id).then(data => {
            if (data.status == 200) {
                setClients(clients().filter(c => c.id != id));
                toast?.info(t('OAUTH_CLIENT_DELETED'));
            }
            else {
                toast?.error(t('OAUTH_CLIENT_DELETE_FAILED'));
            }
        });
    }

    return (
        <div class='dashboard-page'>
            <h1><Text val='MYOAUTHCLIENTS'></Text></h1>
            <h2><Text val='NEWCLIENT'></Text></h2>
            <form onSubmit={submitNewClient} class='fcard h' style={{ "align-items": "center" }}>
                <input
                    type="text"
                    class="search-input"
                    placeholder={t('CLIENT_NAME')}
                    value={newClient().clientName}
                    onInput={(e) => setNewClient({...newClient(), clientName: (e.target as HTMLInputElement).value})}
                />
                <select
                    class="language-select"
                    value={newClient().clientType}
                    onChange={(e) => setNewClient({...newClient(), clientType: parseInt((e.target as HTMLSelectElement).value) as OAuthClientType})}
                >
                    <option value={OAuthClientType.Public}>Public (PKCE)</option>
                    <option value={OAuthClientType.Confidential}>Confidential (Client Secret)</option>
                </select>
                <button aria-label="Add OAuth client"><i class="fa-solid fa-plus" aria-hidden="true"></i></button>
            </form>
            <Show when={loading()}>
                <p class="gray"><Text val="LOADING" /></p>
            </Show>
            <Show when={!loading() && clients().length === 0}>
                <p class="gray"><Text val="OAUTH_CLIENTS_EMPTY" /></p>
            </Show>
            <Show when={clients().length > 0}>
                <table class="sessiontable">
                    <thead>
                        <tr>
                            <th><Text val="NAME"></Text></th>
                            <th>Type</th>
                            <th><Text val="CREATEDAT"></Text></th>
                            <th><i class="fa-solid fa-pen-to-square"></i></th>
                            <th><i class="fa-solid fa-trash"></i></th>
                        </tr>
                    </thead>
                    <tbody>
                        <For each={clients()}>
                            {(client) => (
                                <tr>
                                    <td>{client.clientName}</td>
                                    <td>{client.clientType === OAuthClientType.Confidential ? "Confidential" : "Public"}</td>
                                    <td>{formatDateTime(client.createdAt)}</td>
                                    <td><button onClick={() => navigate("/oauthclient/" + client.id)}><i class="fa-solid fa-pen-to-square"></i></button></td>
                                    <td><button onClick={() => deleteClient(client.id)} class='danger'><i class="fa-solid fa-trash"></i></button></td>
                                </tr>
                            )}
                        </For>
                    </tbody>
                </table>
            </Show>

        </div>
    )
}